import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../data.service';

/**
 *  ISKOLAI CSENGŐ – a csengő-gép vezérlése a Sulinformból.
 *
 *  A csengetés egy Windowsos gépen fut (csengo/school_bell_app.py), ami a Dante-vezérlőbe
 *  játssza a csengőhangot. A gép innen kéri le a napi tervet: a csengetési rendből, a tanév
 *  rendjét (szünet, ünnep, áthelyezett munkanap) is figyelembe véve. Ezen a képernyőn:
 *   - ki-be kapcsolható és szüneteltethető a csengő (a gép ~30 mp-en belül követi);
 *   - egy gombnyomással csengethet, tesztelhet, vagy leállíthat egy épp szóló csengetést;
 *   - óránként beállítható, szól-e a kezdés, a vége és a becsengetés előtti jelzőcsengő
 *     (a jelző perce és hossza a Beállításoknál; a gép más hanggal szólaltatja meg);
 *   - napra szóló eltérés vehető fel (csend, vagy rövidített nap saját időpontokkal);
 *   - kiadható / visszavonható a gép kulcsa, és látszik, mikor csengetett ténylegesen.
 *
 *  A csengetési rend maga NEM itt szerkeszthető: az aSc-importból jön, és tanévváltáskor
 *  másolódik – itt csak azt dönti el az admin, melyik óra szóljon.
 */
@Component({
  selector: 'app-admin-csengo',
  templateUrl: './csengo.component.html',
  styleUrls: ['./csengo.component.scss'],
})
export class AdminCsengoComponent implements OnInit, OnDestroy {
  sajatIntezmeny: any;
  adat: any = null;
  toltodik = false;
  hiba = '';
  mentes = false;
  szunetEddig = '';
  orakModosult = false;

  ujEszkozNev = 'Csengő-gép';
  ujKulcs: { nev: string; kulcs: string } | null = null;

  elteres: any = this.uresElteres();
  elteresNyitva = false;

  private frissito: any = null;

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    this.sajatIntezmeny = await this.data.getFromStorage('sajatIntezmeny');
    await this.betolt();
    //  Az eszköz 30 mp-enként jelentkezik – a képernyő percenként frissül magától, hogy az
    //  online-jelzés és a napló ne ragadjon be (a szerkesztés alatt álló óra-beállítást nem írja felül).
    this.frissito = setInterval(() => { if (!this.orakModosult && !this.elteresNyitva) this.betolt(true); }, 60000);
  }

  ngOnDestroy() {
    if (this.frissito) clearInterval(this.frissito);
  }

  get intezmeny(): string {
    return String(this.sajatIntezmeny?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
  }

  private get q(): string { return `intezmeny=${encodeURIComponent(this.intezmeny)}`; }
  private hibaUzenet(e: any, alap: string) { return e?.error?.error || alap; }

  async betolt(csendben = false) {
    if (!this.intezmeny) return;
    if (!csendben) this.toltodik = true;
    this.hiba = '';
    try {
      this.adat = await firstValueFrom(this.http.get(`/api/csengo/allapot?${this.q}&napok=10`));
      this.szunetEddig = (this.adat?.beallitas?.szunet_eddig || '').replace(' ', 'T').slice(0, 16);
      this.orakModosult = false;
    } catch (e: any) {
      if (!csendben) this.hiba = this.hibaUzenet(e, 'A csengő állapota nem tölthető be.');
    } finally {
      this.toltodik = false;
    }
  }

  get b(): any { return this.adat?.beallitas || {}; }
  get aktivEszkozok(): any[] { return (this.adat?.eszkozok || []).filter((e: any) => e.aktiv); }
  get onlineDb(): number { return this.aktivEszkozok.filter((e: any) => e.online).length; }

  /** Szünetel-e most (a kiszolgáló ideje szerint). */
  get szunetel(): boolean {
    const s = this.b.szunet_eddig;
    return !!s && !!this.adat?.szerver_ido && s > this.adat.szerver_ido;
  }

  /** Mi szól ma még, a kiszolgáló ideje szerint. */
  get kovetkezo(): string {
    const most = String(this.adat?.szerver_ido || '');
    for (const nap of this.adat?.terv || []) {
      for (const c of nap.csengetesek || []) {
        if (`${nap.datum} ${c.time}` >= most.slice(0, 16)) {
          const ma = nap.datum === most.slice(0, 10);
          return `${ma ? 'ma' : nap.datum + ' (' + nap.het_napja + ')'} ${c.time} — ${c.label}`;
        }
      }
    }
    return 'nincs a következő napokban';
  }

  get utolsoCsenges(): any {
    return (this.adat?.naplo || []).find((n: any) => /csenges|teszt/.test(n.esemeny)) || null;
  }

  private async beallitasMent(valtozas: any, uzenet: string) {
    this.mentes = true;
    try {
      const r: any = await firstValueFrom(this.http.put(`/api/csengo/beallitas?${this.q}`, { ...valtozas, intezmeny: this.intezmeny }));
      if (this.adat) this.adat.beallitas = r.beallitas;
      this.data.sendToast(uzenet, 'success');
      await this.betolt(true);
    } catch (e: any) {
      this.data.sendToast(this.hibaUzenet(e, 'A mentés nem sikerült.'), 'error');
      await this.betolt(true);
    } finally {
      this.mentes = false;
    }
  }

  async kapcsol(be: boolean) {
    if (!be && !confirm('Kikapcsolod az iskolai csengőt? Visszakapcsolásig egyetlen csengetés sem szól.')) {
      await this.betolt(true);
      return;
    }
    await this.beallitasMent({ bekapcsolva: be }, be ? 'A csengő bekapcsolva.' : 'A csengő kikapcsolva.');
  }

  async szunetMent() {
    if (!this.szunetEddig) { this.data.sendToast('Válaszd ki, meddig szüneteljen.', 'warning'); return; }
    await this.beallitasMent({ szunet_eddig: this.szunetEddig }, `A csengő szünetel eddig: ${this.szunetEddig.replace('T', ' ')}.`);
  }

  async szunetTorol() {
    this.szunetEddig = '';
    await this.beallitasMent({ szunet_eddig: null }, 'A szüneteltetés megszűnt.');
  }

  async altalanosMent() {
    const jelzoPerc = Number(this.b.jelzo_perc ?? 0);
    const jelzoMp = Number(this.b.jelzo_mp ?? 3);
    if (!Number.isInteger(jelzoPerc) || jelzoPerc < 0 || jelzoPerc > 15) {
      this.data.sendToast('A jelzőcsengő 0 és 15 perc között lehet (0 = nincs).', 'warning');
      return;
    }
    if (!Number.isInteger(jelzoMp) || jelzoMp < 1 || jelzoMp > 30) {
      this.data.sendToast('A jelzőcsengő hossza 1 és 30 mp közé essen.', 'warning');
      return;
    }
    await this.beallitasMent({
      csenges_mp: Number(this.b.csenges_mp),
      hangero: Number(this.b.hangero),
      jelzo_perc: jelzoPerc,
      jelzo_mp: jelzoMp,
      hetvege_nem: !!this.b.hetvege_nem,
      tanev_rendje_szerint: !!this.b.tanev_rendje_szerint,
    }, 'Beállítások mentve.');
  }

  async orakMent() {
    const orak: any = {};
    for (const o of this.adat?.csengetesi_rend?.orak || []) {
      orak[o.oraszam] = { kezdes: !!o.kezdes, vege: !!o.vege, jelzo: !!o.jelzo };
    }
    await this.beallitasMent({ orak }, 'Az órák csengetése mentve.');
  }

  async parancs(parancs: 'csenges_most' | 'teszt' | 'leallitas', eszkoz: string | null = null) {
    const szoveg: any = { csenges_most: 'Csengess most', teszt: 'Teszt csengetés', leallitas: 'Azonnali leállítás' };
    if (parancs === 'csenges_most' && !confirm('Most azonnal megszólaljon a csengő az egész iskolában?')) return;
    try {
      await firstValueFrom(this.http.post(`/api/csengo/parancs?${this.q}`, { parancs, eszkoz, intezmeny: this.intezmeny }));
      this.data.sendToast(`${szoveg[parancs]}: elküldve – a gép fél percen belül végrehajtja.`, 'success');
      await this.betolt(true);
    } catch (e: any) {
      this.data.sendToast(this.hibaUzenet(e, 'A parancs nem küldhető.'), 'error');
    }
  }

  async eszkozFelvesz() {
    try {
      const r: any = await firstValueFrom(this.http.post(`/api/csengo/eszkoz-felvetel?${this.q}`,
        { nev: this.ujEszkozNev, intezmeny: this.intezmeny }));
      this.ujKulcs = { nev: r.nev, kulcs: r.kulcs };
      await this.betolt(true);
    } catch (e: any) {
      this.data.sendToast(this.hibaUzenet(e, 'Az eszköz nem vehető fel.'), 'error');
    }
  }

  async kulcsMasol() {
    if (!this.ujKulcs) return;
    try {
      await navigator.clipboard.writeText(this.ujKulcs.kulcs);
      this.data.sendToast('A kulcs a vágólapon.', 'success');
    } catch (e) {
      this.data.sendToast('A másolás nem sikerült – jelöld ki és másold kézzel.', 'warning');
    }
  }

  async visszavon(e: any) {
    if (!confirm(`Visszavonod a(z) „${e.nev}" kulcsát? A gép ezután nem kap tervet – a gyorsítótárából csenget tovább, amíg új kulcsot nem kap.`)) return;
    try {
      await firstValueFrom(this.http.post(`/api/csengo/eszkoz-visszavonas/${e.id}?${this.q}`, { intezmeny: this.intezmeny }));
      this.data.sendToast('Kulcs visszavonva.', 'success');
      await this.betolt(true);
    } catch (err: any) {
      this.data.sendToast(this.hibaUzenet(err, 'A visszavonás nem sikerült.'), 'error');
    }
  }

  /* ── nap-eltérés ── */

  uresElteres(): any {
    return { id: null, datum: '', tipus: 'nincs', megjegyzes: '', idopontok: [{ time: '', label: '' }] };
  }

  elteresUj() {
    this.elteres = this.uresElteres();
    this.elteresNyitva = true;
  }

  elteresSzerkeszt(e: any) {
    this.elteres = {
      id: e.id, datum: e.datum, tipus: e.tipus, megjegyzes: e.megjegyzes || '',
      idopontok: (e.idopontok?.length ? e.idopontok : [{ time: '', label: '' }]).map((x: any) => ({ ...x })),
    };
    this.elteresNyitva = true;
  }

  /** Rövidített naphoz kiindulásnak a normál rend (utána átírható). */
  elteresRendbol() {
    const nap = (this.adat?.terv || []).find((n: any) => n.forras === 'rend' && n.csengetesek?.length);
    if (!nap) { this.data.sendToast('A következő napokban nincs normál tanítási nap, amiből másolni lehetne.', 'warning'); return; }
    //  A jelzőcsengő nem kerül a listába: az egyedi napon a kiszolgáló maga teszi a
    //  „N. óra kezdés" időpontok elé (ha betennénk, rendes hanggal és hosszal szólna).
    this.elteres.idopontok = nap.csengetesek
      .filter((c: any) => c.fajta !== 'jelzo')
      .map((c: any) => ({ time: c.time, label: c.label }));
  }

  idopontHozzaad() { this.elteres.idopontok.push({ time: '', label: '' }); }
  idopontTorol(i: number) { this.elteres.idopontok.splice(i, 1); }

  async elteresMent() {
    const e = this.elteres;
    const torzs = {
      intezmeny: this.intezmeny, datum: e.datum, tipus: e.tipus, megjegyzes: e.megjegyzes,
      idopontok: e.tipus === 'egyedi' ? e.idopontok.filter((x: any) => x.time) : [],
    };
    try {
      if (e.id) await firstValueFrom(this.http.put(`/api/csengo/elteres/${e.id}?${this.q}`, torzs));
      else await firstValueFrom(this.http.post(`/api/csengo/elteres?${this.q}`, torzs));
      this.data.sendToast('Eltérés mentve.', 'success');
      this.elteresNyitva = false;
      await this.betolt(true);
    } catch (err: any) {
      this.data.sendToast(this.hibaUzenet(err, 'Az eltérés nem menthető.'), 'error');
    }
  }

  async elteresTorol(e: any) {
    if (!confirm(`Törlöd a(z) ${e.datum} napi eltérést? Aznap a szokásos rend szerint szól a csengő.`)) return;
    try {
      await firstValueFrom(this.http.delete(`/api/csengo/elteres/${e.id}?${this.q}`));
      this.data.sendToast('Eltérés törölve.', 'success');
      await this.betolt(true);
    } catch (err: any) {
      this.data.sendToast(this.hibaUzenet(err, 'Az eltérés nem törölhető.'), 'error');
    }
  }

  /* ── megjelenítés ── */

  /** A terv egy csengetésének buboréka fölé: mi szól, és jelzőnél meddig. */
  csengetesCim(c: any): string {
    return c.fajta === 'jelzo' ? `${c.label} (${c.mp || this.b.jelzo_mp} mp, jelzőhanggal)` : c.label;
  }

  esemenyNev(e: string): string {
    const n: any = {
      csenges: 'Csengetés', csenges_most: 'Csengetés (gombnyomásra)', teszt: 'Teszt', leallitas: 'Leállítás',
      kihagyva: 'Kihagyva', hiba: 'Hiba', indulas: 'A program elindult', offline: 'Nem érte el a Sulinformot',
      online: 'Újra eléri a Sulinformot',
    };
    return n[e] || e;
  }

  parancsNev(p: string): string {
    return ({ csenges_most: 'Csengess most', teszt: 'Teszt', leallitas: 'Leállítás' } as any)[p] || p;
  }

  mpOta(mp: any): string {
    const m = Number(mp);
    if (!Number.isFinite(m)) return 'még nem jelentkezett';
    if (m < 90) return `${m} mp-e`;
    if (m < 5400) return `${Math.round(m / 60)} perce`;
    if (m < 172800) return `${Math.round(m / 3600)} órája`;
    return `${Math.round(m / 86400)} napja`;
  }
}
